import type { MaybeRefObjectMaybeProperty } from "@/typings/refs";
import type { Ref } from "vue";
import getDefVal from "@/functions/getValue";
import { isRef } from "vue";

/**
 * @function getProperty - get the value of a possible ref property or return a default value
 * @param {MaybeRefObjectMaybeProperty<ValueType>} theObject - the maybe ref object that possibly has the property
 * @param {string|number} propertyName - the name of the property
 * @param {ValueType} defaultValue - the default value of the property
 * @returns always a ref value, the default or the provided
 */
export default function getProperty<ValueType>(
  theObject: MaybeRefObjectMaybeProperty<ValueType>,
  propertyName: string | number,
  defaultValue: ValueType
): Ref<ValueType> {
  //if the object is undefined directly return the default value
  if (theObject == undefined) {
    return getDefVal(theObject, defaultValue);
  }
  // if the object is a ref
  if (isRef(theObject)) {
    //if the object is a ref but holds an undefined value
    if (theObject.value == undefined) {
      //return the default value
      return getDefVal(theObject.value, defaultValue);
    }
    // if the ref actually holds a value,
    else {
      // verify if its an object as non object values may create confusion and return a non desired value if the
      if (
        typeof theObject.value === "object" &&
        !Array.isArray(theObject.value) &&
        theObject.value !== null
      ) {
        //if the object is clearly an object return the property that may exist or not in the object and is maybe a ref
        return getDefVal(
          theObject.value[propertyName],
          defaultValue
        ) as Ref<ValueType>;
      }
      //in case its not an object and just a value
      else {
        //return the default value, compared against undefined to force the default value
        return getDefVal(undefined, defaultValue);
      }
    }
  }
  // if the object is a not a ref but a value
  else {
    //verify that is an object
    if (
      typeof theObject === "object" &&
      !Array.isArray(theObject) &&
      theObject !== null
    ) {
      // return the property that may exist on the object
      return getDefVal(theObject[propertyName], defaultValue) as Ref<ValueType>;
    }
    //if its not an object
    else {
      //return the default value compared against undefined to force the default value
      return getDefVal(undefined, defaultValue);
    }
  }
}
